import api from 'services/api';
import { REJECT, nullable, num, object, str, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';

/**
 * The request body for both `POST /locks` (create) and `PUT /locks/:zone/:name`
 * (update). **Permissive on purpose** — the server is the validation authority
 * (name pattern, positive limit, zone/folder existence). A too-strict client
 * type would block valid sends when backend rules loosen; the FE authors this
 * body, so there is no response to validate and nothing to drift.
 *
 * Shared by POST and PUT because the FE sends the same shape to both. On update
 * the backend treats each field as "change to this value" (an omitted field is
 * left untouched); the edit model always sends the full set, so every field is
 * written from the form.
 */
export interface SaveLock {
  readonly zone_name: string | null;
  readonly name: string | null;
  readonly limit: number | null;
  readonly description: string | null;
  readonly folder_id: number | null;
}

// POST /locks — create. Backend returns the `INSERT … RETURNING` row (which
// uniquely includes `zone_id`) with `zone_name` echoed from the request and
// `folder_path` from the shared serializer.
//
// `CreatedLock` is its OWN type + schema, even though it overlaps the read
// shapes: it is produced by a distinct backend statement (equal-by-coincidence,
// not identity — see ../../README.md). Structural typing lets it flow wherever a
// `Lock` is expected downstream.
export interface CreatedLock {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly zone_id: number;
  readonly limit: number;
  readonly limit_allocation: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly folder_id: number | null;
  readonly zone_name: string;
  readonly folder_path: FolderPath | null;
}

const createdSchema = object<CreatedLock>({
  id: num(REJECT),
  name: str(),
  description: nullable(str()),
  zone_id: num(),
  limit: num(),
  limit_allocation: num(),
  created_at: str(),
  updated_at: str(),
  folder_id: nullable(num()),
  zone_name: str(),
  folder_path: nullable(folderPathSchema),
});

/**
 * POST `/locks`. Returns the created record.
 * Rejects on HTTP error — the write did not happen, and the server's complaint
 * is the useful thing to report. Answers with the **unreadable** arm when the
 * write DID happen and its receipt cannot be read: by then the server has
 * committed, so that is not a failed save and must not be reported as one
 * (#1035).
 */
export const postLock = async (save: SaveLock): Promise<Received<CreatedLock>> => {
  const res = await api.post('/locks', save);
  return readResponse(res.data, { schema: createdSchema, source: 'postLock' });
};
